import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

// eslint-disable-next-line @nx/enforce-module-boundaries
import type { ChkToastHandlers } from '@ui/checkout-components';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ChkToast } from '@ui/checkout-components';

import { TOAST_VARIANT } from './useToast.constants';
import type {
  SetToast,
  ToastChildren,
  ToastContextValue,
  ToastProviderProps,
  ToastVariant,
} from './useToast.types';

export const Context = createContext<ToastContextValue>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setToast() {},
});

export function useToast() {
  return useContext(Context);
}

export function ToastProvider({
  children,
  preventClickAway,
  closeButtonProps,
}: ToastProviderProps) {
  const [toastChildren, setToastChildren] = useState<ToastChildren>(undefined);
  const [toastVariant, setToastVariant] = useState<ToastVariant>(
    TOAST_VARIANT.INFO
  );
  const [announceVersion, setAnnounceVersion] = useState(0);
  const toastRef = useRef<ChkToastHandlers>(null);
  const [open, setOpen] = useState<boolean>(false);

  const setToast: SetToast = useCallback(
    (children, variant = TOAST_VARIANT.INFO) => {
      setToastChildren(children);
      setToastVariant(variant);
      setOpen(true);

      setAnnounceVersion((prev) => prev + 1);
    },
    []
  );

  useEffect(() => {
    if (announceVersion > 0) {
      toastRef.current?.focusToast();
    }
  }, [announceVersion]);

  return (
    <Context.Provider value={{ setToast }}>
      {children}

      <ChkToast
        autoHideDuration={60_000}
        open={open}
        alignCloseIcon="flex-start"
        onClose={() => setOpen(false)}
        children={toastChildren}
        variant={toastVariant}
        closeButtonProps={{
          ...closeButtonProps,
        }}
        {...(preventClickAway && {
          ClickAwayListenerProps: {
            mouseEvent: false,
            touchEvent: false,
          },
        })}
        ref={toastRef}
      />
    </Context.Provider>
  );
}

export const withToastProvider = ({ children }: ToastProviderProps) => (
  <ToastProvider>{children}</ToastProvider>
);
